<template>
  <div class="w-100 px-2"
       :class="{'input-padding': !!prependInnerText || !!appendInnerText}">
    <flex-layout v-if="props.label" class="w-100">
      <div>
        <base-text :color="labelColor">{{ props.label }}</base-text>
      </div>
      <required-tag-text v-if="props.requiredTag || props.primaryRequiredTag" :color="props.primaryRequiredTag? 'primary' : 'error'">必須</required-tag-text>
    </flex-layout>
    <base-text v-if="props.hint" size="xs" v-html="props.hint"/>
    <div class="input-row">
      <slot name="prepend"/>
      <div class="input-container">
        <slot name="prepend-inner"/>
        <div v-if="prependInnerText" class="prepend-input" :class="prependInnerClass">
          <base-text no-wrap>{{ prependInnerText }}</base-text>
        </div>
        <input
            class="w-100"
            :class="{ 'invalidField': !!validate(rules, props.modelValue) }"
            :autocomplete="props.autocomplete"
            :value="formattedValue"
            type="text"
            :placeholder="props.placeholder"
            :maxlength="props.maxLength"
            :disabled="props.disabled"
            :readonly="props.readonly"
            @input="onInput"
        >
        <div class="input-append-inner">
          <base-icon
            v-if="props.clearable"
            class="clickable"
            width="20px"
            height="20px"
            :stroke="props.disabled ? 'gray': ''"
            name="cross-sign"
            @click.stop="onClear"
          />
          <slot name="append-inner"/>
        </div>
        <div v-if="appendInnerText" class="append-input" :class="appendInnerClass">
          <base-text no-wrap>{{ appendInnerText }}</base-text>
        </div>
      </div>
    </div>
    <base-text v-if="details" class="px-2" size="xs" :no-wrap="noWrapDetails">
      {{ details }}
      <slot name="details"/>
    </base-text>
    <base-text v-if="!props.noError" color="error" size="xs"> {{ errorMessage }}</base-text>
  </div>
</template>

<script lang="ts" setup>
import {computed, ref, toRef} from 'vue';
import BaseText from '@/components/Texts/BaseText.vue';
import FlexLayout from '@/components/Layouts/FlexLayout.vue';
import RequiredTagText from '@/components/Texts/RequiredTagText.vue';
import {ValidatorRule} from "@/composable/ui/useForm";
import useFormField from "@/composable/ui/useFormField";
import BaseIcon from "@/components/Images/BaseIcon.vue";
import {useValidator} from "@/composable/ui/useValidator";

const { validate } = useValidator();


const props = defineProps(
    {
      label: String,
      modelValue: [String, Number],
      placeholder: String,
      type: {
        type: String as () => 'number' | 'integer',
        default: 'number',
      },
      rules: {
        type: Array as () => ValidatorRule<string>[],
        default: () => [],
      },
      requiredTag: {
        type: Boolean,
        default: false,
      },
      maxLength: {
        type: Number,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      hint: {
        type: String,
        default: '',
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      noError: {
        type: Boolean,
        default: false,
      },
      details: {
        type: String,
        default: '',
      },
      labelColor: {
        type: String,
        default: 'primary',
      },
      autocomplete: {
        type: String as () => 'on' | 'off' | "one-time-code",
        default: 'off',
      },
      prependInnerText: {
        type: String,
        default: '',
      },
      appendInnerText: {
        type: String,
        default: '',
      },
      prependInnerClass: {
        type: String,
        default: '',
      },
      appendInnerClass: {
        type: String,
        default: '',
      },
      noWrapDetails: {
        type: Boolean,
        default: false,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      primaryRequiredTag: {
        type: Boolean,
        default: false,
      },
    },
);
const emit = defineEmits(['update:modelValue', 'clear']);
const inputRef = ref<HTMLInputElement>();
const form = useFormField(toRef(() => props.modelValue), toRef(() => props.rules));
const errorMessage = computed(() => {
  return form.error.value;
})

const numberRegex = /[^0-9.]/g;
const integerRegex = /[^\d\uFF10-\uFF19]/g;

const formattedValue = computed(() => {
    let value = props.modelValue;
    if (props.type === 'integer') value = value ? String(props.modelValue).replace(integerRegex, '') : props.modelValue;
    else value = value ? String(props.modelValue).replace(numberRegex, '') : props.modelValue;
    return value;
});

async function onInput(e: Event) {
    let value = (e.target as HTMLInputElement).value;
    await emit('update:modelValue', value);
    if (props.type === 'integer') value = value.replace(integerRegex, '');
    else value = value.replace(numberRegex, '');
    emit('update:modelValue', value);
}

function onClear() {
  emit('update:modelValue', '');
  emit('clear');
}
</script>

<style scoped>
input {
  font-size: 18px;
  background: transparent;
  border: none;
  outline: none;
  padding-left: 12px;
}

.required {
  border: solid var(--error) 1px;
  padding: 5px;
  font-size: .7rem;
  color: var(--error);
  height: 20px;
  line-height: 8px;
  background: #fff;
  min-width: 40px;
}

::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: var(--placeholder);
  opacity: 1; /* Firefox */
}

:-ms-input-placeholder { /* Internet Explorer 10-11 */
  color: red;
}

::-ms-input-placeholder { /* Microsoft Edge */
  color: red;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}

.input-row {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.input-container {
  position: relative;
  width: 100%;
  height: 50px;
  display: flex;
  justify-content: center;
  align-content: center;
  border-radius: 5px;
  border: 1px solid var(--border);
  background-color: var(--grayLight) !important;
}

.input-container:focus-within {
  border: solid var(--primary) 1px;
}

.input-append-inner {
  display: flex;
  align-items: center;
  padding: 5px;
}

.input-padding:deep(.input-container) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.prepend-input {
  background: var(--lighterGray);
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  min-width: 53px;
  padding-top: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: solid var(--border) 1px;
}
.append-input {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 53px;
  padding-top: 8px;
  background: var(--lighterGray);
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  border-left: solid var(--border) 1px;
}
</style>
